import posthog from 'posthog-js';
import type { Properties } from 'posthog-js';

const posthogToken = process.env.REACT_APP_POSTHOG_KEY;
const posthogHost =
  process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const environment = process.env.REACT_APP_ENVIRONMENT;
const APP_NAME = 'Partna dashboard';
const DEFAULT_IGNORED_STATUS_CODES = [400, 422, 401];

type SanitizedValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | boolean[];

interface CaptureErrorOptions {
  context?: string;
  metadata?: Record<string, unknown>;
  captureExpected?: boolean;
  ignoreStatuses?: number[];
  source?: string;
}

let isPostHogInitialized = false;
let areGlobalExceptionHandlersRegistered = false;
let currentUserMetadata: Properties = {};

const isPostHogEnabled = () => {
  return Boolean(posthogToken);
};

const isRealRuntimeEnvironment = () => {
  return typeof window !== 'undefined' && !(window as any).Cypress;
};

export const initPostHog = () => {
  if (
    isPostHogInitialized ||
    !isRealRuntimeEnvironment() ||
    !isPostHogEnabled()
  ) {
    return;
  }

  posthog.init(posthogToken as string, {
    api_host: posthogHost,
    defaults: '2026-05-30',
    autocapture: false,
    capture_pageview: false,
    mask_all_text: true,
    mask_all_element_attributes: true,
  });

  isPostHogInitialized = true;
};

export const registerPostHogExceptionHandlers = () => {
  if (areGlobalExceptionHandlersRegistered || !isRealRuntimeEnvironment()) {
    return;
  }

  window.addEventListener('error', (event) => {
    captureError(event.error || event.message, {
      context: 'window error',
      source: 'window_error',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    captureError(event.reason, {
      context: 'unhandled promise rejection',
      source: 'unhandled_rejection',
    });
  });

  areGlobalExceptionHandlersRegistered = true;
};

export const captureError = (
  error: unknown,
  {
    context,
    metadata = {},
    captureExpected = false,
    ignoreStatuses,
    source,
  }: CaptureErrorOptions = {}
) => {
  if (!isPostHogInitialized) {
    return;
  }

  const status = getErrorStatus(error);
  const ignoredStatuses = ignoreStatuses || DEFAULT_IGNORED_STATUS_CODES;

  if (!captureExpected && status && ignoredStatuses.includes(status)) {
    return;
  }

  const errorProperties = sanitizeErrorProperties({
    ...currentUserMetadata,
    ...metadata,
    app: APP_NAME,
    app_name: APP_NAME,
    context,
    environment,
    route: window.location.pathname,
    source,
    status,
  });

  const properties = {
    ...errorProperties,
    replay_trigger_event: 'exception_session_replay_triggered',
  };

  posthog.capture('exception_session_replay_triggered', {
    ...properties,
    trigger_reason: 'exception',
  });

  posthog.captureException(normalizeError(error), properties);
};

export const identifyPostHogUser = (user: {
  id?: string;
  email?: string;
  username?: string;
  fullName?: string;
  accountName?: string;
  roles?: string[];
}) => {
  if (!isPostHogInitialized) {
    return;
  }

  const distinctId = user.id || user.username || user.email;

  if (!distinctId) {
    return;
  }

  currentUserMetadata = {
    accountName: user.accountName,
    account_name: user.accountName,
    app: APP_NAME,
    app_name: APP_NAME,
    email: user.email,
  };

  posthog.identify(distinctId, {
    ...currentUserMetadata,
    email: user.email,
    username: user.username,
    name: user.fullName,
    roles: user.roles,
    environment,
  });
};

export const resetPostHogUser = () => {
  if (!isPostHogInitialized) {
    return;
  }

  currentUserMetadata = {};
  posthog.reset();
};

const sanitizeErrorProperties = (
  properties: Record<string, unknown>
): Properties => {
  const allowedKeys = new Set([
    'accountName',
    'account_name',
    'app',
    'app_name',
    'colno',
    'component_stack',
    'context',
    'email',
    'environment',
    'errorCode',
    'filename',
    'lineno',
    'method',
    'provider',
    'referenceId',
    'replay_trigger_event',
    'route',
    'source',
    'status',
    'statusCode',
    'transactionId',
    'trigger_reason',
    'url',
  ]);

  return Object.entries(properties).reduce<Properties>(
    (safeProperties, [key, value]) => {
      if (!allowedKeys.has(key) || value === undefined || value === null) {
        return safeProperties;
      }

      const sanitizedValue =
        key === 'url' ? sanitizeUrl(value) : sanitizeValue(value);

      if (sanitizedValue !== undefined) {
        safeProperties[key] = sanitizedValue;
      }

      return safeProperties;
    },
    {}
  );
};

const sanitizeUrl = (value: unknown) => {
  if (typeof value !== 'string') {
    return undefined;
  }

  return value.split('?')[0];
};

const sanitizeValue = (value: unknown): SanitizedValue | undefined => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'string' ||
        typeof item === 'number' ||
        typeof item === 'boolean'
    )
  ) {
    return value as SanitizedValue;
  }
};

const getErrorStatus = (error: unknown) => {
  const errorObject = error as {
    response?: { status?: number };
    status?: number;
    statusCode?: number;
  };

  return (
    errorObject?.response?.status ||
    errorObject?.status ||
    errorObject?.statusCode
  );
};

const normalizeError = (error: unknown) => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  const errorObject = error as {
    message?: string;
    response?: { data?: { message?: string } };
  };

  return new Error(
    errorObject?.response?.data?.message ||
      errorObject?.message ||
      'Unknown application error'
  );
};
